import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Textarea,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Code,
  Collapse,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon, DownloadIcon, CopyIcon } from "@chakra-ui/icons";
import { useDropzone } from "react-dropzone";

const sampleCSV = `name,address
Home,123 Main St, Boston MA
Office,456 Park Ave, New York NY
Store,789 Broadway, Los Angeles CA`;

const sampleJSON = `[
  {"name": "Home", "address": "123 Main St, Boston MA"},
  {"name": "Office", "address": "456 Park Ave, New York NY"},
  {"name": "Store", "address": "789 Broadway, Los Angeles CA"}
]`;

const sampleHierarchical = `{
  "Baptist Memorial Hospital - Memphis": [
    { "id": "14941", "name": "ER", "address": "" },
    { "id": "145106", "name": "Labor & Delivery", "address": "" }
  ],
  "Memphis Fire Department": [
    { "id": "1", "name": "Medic 12", "address": "" },
    { "id": "2", "name": "Medic 19", "address": "" }
  ]
}`;

// Simple CSV parser
const parseCSV = (csvText) => {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) {
    throw new Error("CSV must have at least a header row and one data row");
  }

  const headers = lines[0].split(",").map((h) => h.trim());
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    if (values.length === headers.length) {
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      data.push(row);
    }
  }

  return data;
};

// Convert results to CSV
const convertToCSV = (data) => {
  if (!data || data.length === 0) return "";

  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(",")];

  for (const row of data) {
    const values = headers.map((header) => {
      const value = row[header];
      return typeof value === "object" && value !== null ? JSON.stringify(value) : String(value || "");
    });
    csvRows.push(values.join(","));
  }

  return csvRows.join("\n");
};

export default function AddressLookup() {
  const [inputText, setInputText] = useState("");
  const [format, setFormat] = useState("csv");
  const [parsedData, setParsedData] = useState(null);
  const [isHierarchical, setIsHierarchical] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [showSamples, setShowSamples] = useState(false);
  const toast = useToast();

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        setInputText(e.target.result);
      };
      reader.readAsText(file);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/json": [".json"],
    },
    maxFiles: 1,
  });

  const handleParse = () => {
    try {
      let data;
      let hierarchical = false;
      
      if (format === "csv") {
        data = parseCSV(inputText);
      } else if (format === "hierarchical") {
        const parsed = JSON.parse(inputText);
        // Check if it's hierarchical format (object with agency keys)
        if (typeof parsed === "object" && !Array.isArray(parsed)) {
          data = parsed;
          hierarchical = true;
        } else {
          throw new Error("Hierarchical format must be an object with agency names as keys");
        }
      } else {
        data = JSON.parse(inputText);
      }

      if (hierarchical) {
        // Validate hierarchical structure
        const agencies = Object.keys(data);
        if (agencies.length === 0) {
          throw new Error("Hierarchical data must contain at least one agency");
        }
        
        let totalSites = 0;
        for (const agency of agencies) {
          if (!Array.isArray(data[agency])) {
            throw new Error(`Agency "${agency}" must have an array of sites`);
          }
          totalSites += data[agency].length;
        }

        setIsHierarchical(true);
        setParsedData(data);
        toast({
          title: "Data parsed successfully",
          description: `Found ${agencies.length} agencies with ${totalSites} sites to process`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Validate flat structure
        if (!Array.isArray(data) || data.length === 0) {
          throw new Error("Data must be a non-empty array");
        }

        if (!data[0].address) {
          throw new Error("Each item must have an 'address' field");
        }

        setIsHierarchical(false);
        setParsedData(data);
        toast({
          title: "Data parsed successfully",
          description: `Found ${data.length} addresses to process`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Parse error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleProcess = async () => {
    if (!parsedData) return;

    setProcessing(true);
    setProgress(0);
    setResults(null);

    try {
      if (isHierarchical) {
        // Process hierarchical data
        const hierarchicalResults = {};
        const agencies = Object.keys(parsedData);
        let processedAgencies = 0;

        for (const agency of agencies) {
          const sites = parsedData[agency];
          
          // Process sites for this agency
          const response = await fetch("/maps/batch-geocode", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ 
              addresses: sites,
              agencyName: agency 
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const agencyResults = await response.json();
          hierarchicalResults[agency] = agencyResults;

          processedAgencies++;
          setProgress(Math.round((processedAgencies / agencies.length) * 100));

          // Small delay between agencies
          if (processedAgencies < agencies.length) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        setResults(hierarchicalResults);
        toast({
          title: "Processing complete",
          description: `Successfully processed ${agencies.length} agencies`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Process flat data
        const batchSize = 10;
        const allResults = [];
        const totalBatches = Math.ceil(parsedData.length / batchSize);

        for (let i = 0; i < parsedData.length; i += batchSize) {
          const batch = parsedData.slice(i, i + batchSize);
          const currentBatch = Math.floor(i / batchSize) + 1;

          const response = await fetch("/maps/batch-geocode", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ addresses: batch }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const batchResults = await response.json();
          allResults.push(...batchResults);

          setProgress(Math.round((currentBatch / totalBatches) * 100));

          if (i + batchSize < parsedData.length) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        setResults(allResults);
        toast({
          title: "Processing complete",
          description: `Successfully processed ${allResults.length} addresses`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Processing error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = (downloadFormat) => {
    if (!results) return;

    let content, filename, mimeType;

    if (downloadFormat === "csv") {
      if (isHierarchical) {
        // Flatten hierarchical data for CSV
        const flatData = [];
        for (const [agency, sites] of Object.entries(results)) {
          for (const site of sites) {
            flatData.push({
              agency,
              ...site
            });
          }
        }
        content = convertToCSV(flatData);
      } else {
        content = convertToCSV(results);
      }
      filename = "geocoded-addresses.csv";
      mimeType = "text/csv";
    } else {
      // JSON preserves structure
      content = JSON.stringify(results, null, 2);
      filename = "geocoded-addresses.json";
      mimeType = "application/json";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Download started",
      description: `Downloading ${filename}`,
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCopyToClipboard = () => {
    if (!results) return;

    const text = JSON.stringify(results, null, 2);
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: "Copied to clipboard",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      },
      () => {
        toast({
          title: "Failed to copy",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
      }
    );
  };

  const loadSample = (sampleFormat) => {
    if (sampleFormat === "csv") {
      setInputText(sampleCSV);
      setFormat("csv");
    } else if (sampleFormat === "hierarchical") {
      setInputText(sampleHierarchical);
      setFormat("hierarchical");
    } else {
      setInputText(sampleJSON);
      setFormat("json");
    }
    toast({
      title: "Sample data loaded",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const getAddressSourceBadge = (addressSource) => {
    if (!addressSource) return null;
    
    const configs = {
      original: { color: "green", text: "Original" },
      memphis_fire_lookup: { color: "blue", text: "Memphis Fire Lookup" },
      agency_fallback: { color: "yellow", text: "Agency Fallback" }
    };
    
    const config = configs[addressSource] || { color: "gray", text: addressSource };
    return <Badge colorScheme={config.color}>{config.text}</Badge>;
  };

  const getStatusColor = (result) => {
    if (!result.success) return "red.500";
    if (result.address_source === "agency_fallback") return "yellow.500";
    return "green.500";
  };

  let successCount = 0;
  let failCount = 0;
  
  if (results) {
    if (isHierarchical) {
      for (const sites of Object.values(results)) {
        successCount += sites.filter((r) => r.success).length;
        failCount += sites.filter((r) => !r.success).length;
      }
    } else {
      successCount = results.filter((r) => r.success).length;
      failCount = results.length - successCount;
    }
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box>
          <Heading size="xl" mb={2}>
            Batch Address Lookup
          </Heading>
          <Text color="gray.600">
            Upload a CSV or JSON file containing addresses, or paste data directly. Each address will be geocoded to
            get its coordinates and formatted address.
          </Text>
        </Box>

        {/* Sample Formats */}
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontWeight="bold">Sample Formats</Text>
            <IconButton
              size="sm"
              icon={showSamples ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={() => setShowSamples(!showSamples)}
              aria-label="Toggle samples"
            />
          </HStack>
          <Collapse in={showSamples}>
            <VStack align="stretch" spacing={4}>
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold">CSV Format:</Text>
                  <Button size="sm" onClick={() => loadSample("csv")}>
                    Load CSV Sample
                  </Button>
                </HStack>
                <Code display="block" whiteSpace="pre" p={3} borderRadius="md">
                  {sampleCSV}
                </Code>
              </Box>
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold">JSON Format:</Text>
                  <Button size="sm" onClick={() => loadSample("json")}>
                    Load JSON Sample
                  </Button>
                </HStack>
                <Code display="block" whiteSpace="pre" p={3} borderRadius="md">
                  {sampleJSON}
                </Code>
              </Box>
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="semibold">Hierarchical JSON (Agency/Sites):</Text>
                  <Button size="sm" onClick={() => loadSample("hierarchical")}>
                    Load Hierarchical Sample
                  </Button>
                </HStack>
                <Code display="block" whiteSpace="pre" p={3} borderRadius="md">
                  {sampleHierarchical}
                </Code>
              </Box>
            </VStack>
          </Collapse>
        </Box>

        <Divider />

        {/* Input Section */}
        <Box>
          <Heading size="md" mb={4}>
            Input Data
          </Heading>

          {/* File Upload Dropzone */}
          <Box
            {...getRootProps()}
            p={8}
            border="2px dashed"
            borderColor={isDragActive ? "blue.400" : "gray.300"}
            borderRadius="md"
            textAlign="center"
            cursor="pointer"
            bg={isDragActive ? "blue.50" : "gray.50"}
            mb={4}
            _hover={{ borderColor: "blue.400", bg: "blue.50" }}
          >
            <input {...getInputProps()} />
            <Text fontWeight="semibold" mb={2}>
              {isDragActive ? "Drop the file here" : "Drag & drop a CSV or JSON file here"}
            </Text>
            <Text fontSize="sm" color="gray.600">
              or click to select a file
            </Text>
          </Box>

          {/* Format Selector */}
          <HStack mb={4}>
            <Text fontWeight="semibold">Format:</Text>
            <Select value={format} onChange={(e) => setFormat(e.target.value)} maxW="300px">
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="hierarchical">Hierarchical JSON (Agency/Sites)</option>
            </Select>
          </HStack>

          {/* Text Input */}
          <Textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={`Paste your ${format.toUpperCase()} data here...`}
            rows={8}
            fontFamily="monospace"
            mb={4}
          />

          <Button colorScheme="blue" onClick={handleParse} isDisabled={!inputText.trim()}>
            Parse Data
          </Button>
        </Box>

        {/* Preview Section */}
        {parsedData && (
          <>
            <Divider />
            <Box>
              {isHierarchical ? (
                <>
                  <Alert status="success" mb={4}>
                    <AlertIcon />
                    <AlertTitle>Data Preview</AlertTitle>
                    <AlertDescription>
                      Found {Object.keys(parsedData).length} agencies with{" "}
                      {Object.values(parsedData).reduce((acc, sites) => acc + sites.length, 0)} sites ready to process
                    </AlertDescription>
                  </Alert>
                  <Accordion allowMultiple>
                    {Object.entries(parsedData).slice(0, 3).map(([agency, sites]) => (
                      <AccordionItem key={agency}>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              {agency} ({sites.length} sites)
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>#</Th>
                                  {Object.keys(sites[0] || {}).map((key) => (
                                    <Th key={key}>{key}</Th>
                                  ))}
                                </Tr>
                              </Thead>
                              <Tbody>
                                {sites.slice(0, 3).map((site, index) => (
                                  <Tr key={index}>
                                    <Td>{index + 1}</Td>
                                    {Object.values(site).map((value, i) => (
                                      <Td key={i}>{String(value)}</Td>
                                    ))}
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                          {sites.length > 3 && (
                            <Text fontSize="sm" color="gray.600" mt={2}>
                              Showing first 3 of {sites.length} sites
                            </Text>
                          )}
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {Object.keys(parsedData).length > 3 && (
                    <Text fontSize="sm" color="gray.600" mt={2}>
                      Showing first 3 of {Object.keys(parsedData).length} agencies
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Alert status="success" mb={4}>
                    <AlertIcon />
                    <AlertTitle>Data Preview</AlertTitle>
                    <AlertDescription>Found {parsedData.length} addresses ready to process</AlertDescription>
                  </Alert>

                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>#</Th>
                          {Object.keys(parsedData[0]).map((key) => (
                            <Th key={key}>{key}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {parsedData.slice(0, 5).map((row, index) => (
                          <Tr key={index}>
                            <Td>{index + 1}</Td>
                            {Object.values(row).map((value, i) => (
                              <Td key={i}>{String(value)}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {parsedData.length > 5 && (
                    <Text fontSize="sm" color="gray.600" mt={2}>
                      Showing first 5 of {parsedData.length} addresses
                    </Text>
                  )}
                </>
              )}
            </Box>
          </>
        )}

        {/* Processing Section */}
        {parsedData && (
          <>
            <Divider />
            <Box>
              <Heading size="md" mb={4}>
                Process Addresses
              </Heading>

              <Button
                colorScheme="green"
                size="lg"
                onClick={handleProcess}
                isLoading={processing}
                loadingText={`Processing (${progress}%)`}
                isDisabled={processing}
              >
                Start Geocoding
              </Button>

              {processing && (
                <Box mt={4}>
                  <Progress value={progress} size="lg" colorScheme="green" hasStripe isAnimated />
                  <Text mt={2} fontSize="sm" color="gray.600">
                    {isHierarchical
                      ? `Processing ${Object.keys(parsedData).length} agencies... ${progress}% complete`
                      : `Processing ${parsedData.length} addresses... ${progress}% complete`}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Please wait, this may take a few moments
                  </Text>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Results Section */}
        {results && (
          <>
            <Divider />
            <Box>
              <Heading size="md" mb={4}>
                Results
              </Heading>

              {/* Summary Stats */}
              <StatGroup mb={6}>
                <Stat>
                  <StatLabel>Total Processed</StatLabel>
                  <StatNumber>{results.length}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Successful</StatLabel>
                  <StatNumber color="green.500">{successCount}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Failed</StatLabel>
                  <StatNumber color="red.500">{failCount}</StatNumber>
                </Stat>
              </StatGroup>

              {/* Action Buttons */}
              <HStack spacing={4} mb={6}>
                <Button leftIcon={<DownloadIcon />} colorScheme="blue" onClick={() => handleDownload("csv")}>
                  Download CSV
                </Button>
                <Button leftIcon={<DownloadIcon />} colorScheme="blue" onClick={() => handleDownload("json")}>
                  Download JSON
                </Button>
                <Button leftIcon={<CopyIcon />} variant="outline" onClick={handleCopyToClipboard}>
                  Copy to Clipboard
                </Button>
              </HStack>

              {/* Results Preview */}
              <Box overflowX="auto">
                <Text fontWeight="bold" mb={2}>
                  Preview (First 10 Results)
                </Text>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>#</Th>
                      <Th>Status</Th>
                      <Th>Input Address</Th>
                      <Th>Formatted Address</Th>
                      <Th>Coordinates</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {results.slice(0, 10).map((result, index) => (
                      <Tr key={index}>
                        <Td>{index + 1}</Td>
                        <Td>
                          <Text color={result.success ? "green.500" : "red.500"} fontWeight="bold">
                            {result.success ? "✓" : "✗"}
                          </Text>
                        </Td>
                        <Td>{result.originalAddress}</Td>
                        <Td>{result.formattedAddress || "-"}</Td>
                        <Td>
                          {result.location
                            ? `${result.location.lat.toFixed(6)}, ${result.location.lng.toFixed(6)}`
                            : "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {results.length > 10 && (
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Showing first 10 of {results.length} results. Download the full file to see all results.
                </Text>
              )}
            </Box>
          </>
        )}
      </VStack>
    </Container>
  );
}